import { useEffect, useRef, useState, useCallback } from "react";
import {
  Box,
  Button,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import useWebSocket from "react-use-websocket";
import { SERVER_URL, WEBSOCKET_URL } from "../../store/config";
import { useExportCommit } from "../../store/exportCommit/hooks";
import { useAuth } from "../../store/auth/hooks";
import { ContentTitleBar } from "../../components/ContentTitleBar";
import { getNavigationUrl } from "../../store/navigationUtil";
import { WarningAmber } from "@mui/icons-material";
import ExportStatus from "../../components/export/ExportStatus";
import { loadClip } from "./utils/dataLoader";
import { humanFileSize } from "../../store/formatUtil";

export default function ExportPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const {
    exportStatus,
    fileCount,
    totalBytes,
    guid,
    isDownloadReady,
    isDownloading,
    percentComplete,
    downloadingExportCommit,
    queueDownloadExportCommit,
    startDownloadExportCommit,
    statusUpdateExportCommit,
    reset: exportCommitReset,
  } = useExportCommit();
  const { accessCode } = useAuth();
  const formRef = useRef();
  const { caseId, clipId } = useParams();
  const downloadUrl = `${SERVER_URL}/api/export/${guid}`;
  const [websocketRegistered, setWebsocketRegistered] = useState(false);
  const [clip, setClip] = useState(null);

  const load = useCallback(async () => {
    const currClip = await loadClip(caseId, clipId);
    if (!currClip) {
      navigate(getNavigationUrl(caseId));
    }
    setClip(currClip);
    exportCommitReset();
    queueDownloadExportCommit(caseId, clipId);
  }, [caseId, clipId, exportCommitReset, navigate, queueDownloadExportCommit]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (formRef && isDownloadReady && !isDownloading) {
      formRef.current.submit();
      console.log(">>>>>>>>>>>>>>>>>>>>>>>>>");
      console.log(formRef);
      console.log(">>>>>>>>>>>>>>>>>>>>>>>>>");
    }
  }, [formRef, isDownloadReady, isDownloading]);

  /**
   * Setup a websocket
   */
  const { sendMessage } = useWebSocket(WEBSOCKET_URL, {
    share: true,
    onOpen: () => {
      console.log("WebSocket connection established.", WEBSOCKET_URL);
    },
    onMessage: (event) => {
      if (event.data && event.data) {
        const data = JSON.parse(event.data);
        const payload = data.data;

        if (
          data.command === "EXPORT_STATUS" &&
          payload.clipId === Number(clipId)
        ) {
          statusUpdateExportCommit(
            payload.status,
            payload.guid,
            payload.percentComplete,
          );
          if (payload.status === "DOWNLOAD_READY") {
            startDownloadExportCommit();
          }
          if (payload.status === "DOWNLOADING") {
            downloadingExportCommit();
          }
          if (payload.status === "DOWNLOAD_COMPLETE") {
            exportCommitReset();
            navigate(getNavigationUrl(caseId));
          }
        }
      }
    },
  });

  useEffect(() => {
    if (!websocketRegistered) {
      setWebsocketRegistered(true);
      const payload = {
        command: "REGISTER",
        authorization: accessCode,
      };
      sendMessage(JSON.stringify(payload));
    }
  }, [accessCode, websocketRegistered, sendMessage]);

  return (
    <Stack component="main" height={1}>
      <Stack flex={1} gap={2} p={2}>
        <Paper elevation={4} sx={{ p: 2 }}>
          <Grid container spacing={2} justifyContent="space-between">
            <Grid
              size={{ xs: 12, sm: 3 }}
              display="flex"
              flexDirection="column"
              sx={{ minWidth: "fit-content" }}
            >
              <Typography variant="caption">EXPORT STATUS</Typography>
              <Box alignSelf="start">
                <ExportStatus />
              </Box>
            </Grid>
            <Grid
              size={{ xs: 12, sm: 3 }}
              display="flex"
              flexDirection="column"
            >
              <Typography variant="caption">PROGRESS</Typography>
              <Typography variant="h5">{percentComplete}%</Typography>
            </Grid>
            <Grid
              size={{ xs: 12, sm: 3 }}
              display="flex"
              flexDirection="column"
            >
              <Typography variant="caption">File Count</Typography>
              <Typography variant="h5">{fileCount}</Typography>
            </Grid>
            <Grid
              size={{ xs: 12, sm: 3 }}
              display="flex"
              flexDirection="column"
            >
              <Typography variant="caption">EXPORT SIZE</Typography>
              <Typography variant="h5">{humanFileSize(totalBytes)}</Typography>
            </Grid>
          </Grid>
        </Paper>
        <Paper
          elevation={4}
          sx={{
            display: "flex",
            alignItems: "center",
            backgroundColor: theme.palette.warning.main,
            color: theme.palette.common.white,
            p: 2,
          }}
        >
          <WarningAmber />
          <Stack justifyContent="start" ml={2}>
            <Typography variant="body1">
              You must remain on this screen. Exports can take a while. Please
              be patient.
            </Typography>
            <Typography variant="caption">
              It may take a couple of minutes to prepare the download. The
              download will start automatically when it is ready. Please stay on
              this page until the download has begun.
            </Typography>
          </Stack>
        </Paper>
        {exportStatus === "DOWNLOAD_READY" && (
          <Paper elevation={4} sx={{ p: 2 }}>
            <form
              ref={formRef}
              action={downloadUrl}
              id="downloadForm"
              method="post"
            >
              <input name="auth" type="hidden" value="[[accessToken]]" />
            </form>
            <Typography variant="body1" noWrap color="textPrimary">
              If your download does not start automatically, click{" "}
              <Link
                sx={{ cursor: "pointer" }}
                onClick={() => startDownloadExportCommit()}
              >
                HERE
              </Link>
              .
            </Typography>
          </Paper>
        )}
      </Stack>
      <Paper
        elevation={4}
        sx={{
          p: 1,
          mx: 1,
          mb: 1,
        }}
      >
        <Button
          variant="outlined"
          onClick={async () => {
            await exportCommitReset();
            navigate(`/app/cases/${caseId}/clips`);
          }}
        >
          CANCEL EXPORT
        </Button>
      </Paper>
      <ContentTitleBar name={clip?.description} label="Export Clip" />
    </Stack>
  );
}
